import React, { forwardRef, useCallback, useLayoutEffect, useRef, useState } from "react";

type PropsDef = React.TextareaHTMLAttributes<HTMLTextAreaElement> & {
  // The field's current value. react-hook-form textareas are uncontrolled, so
  // a reset() never re-renders this component — the parent passes the watched
  // value in so the height follows it.
  watchedValue?: string;
  maxHeight?: number;
};

// CN-2808: a one-line textarea that grows with its text, so two notes on the
// session pop-up don't cost two fixed 80px boxes of vertical space.
const AutoGrowTextarea = forwardRef<HTMLTextAreaElement, PropsDef>(
  function AutoGrowTextarea(
    { watchedValue, maxHeight = 200, onInput, onFocus, onBlur, style, ...rest },
    ref
  ) {
    const innerRef = useRef<HTMLTextAreaElement | null>(null);
    // While the field is being edited, the cap comes off and the whole text is
    // in view — editing the middle of a long note through a 200px window means
    // arrowing around blind. The modal body scrolls, so the extra height has
    // somewhere to go. On blur the cap comes back.
    const [focused, setFocused] = useState(false);

    const resize = useCallback(() => {
      const el = innerRef.current;
      if (!el) {
        return;
      }
      el.style.height = "auto";
      // scrollHeight excludes the border; add it back so a single line
      // doesn't show a scrollbar.
      const border = el.offsetHeight - el.clientHeight;
      const next = el.scrollHeight + border;
      const cap = focused ? next : maxHeight;
      el.style.height = `${Math.min(next, cap)}px`;
      el.style.overflowY = next > cap ? "auto" : "hidden";
    }, [maxHeight, focused]);

    useLayoutEffect(resize, [resize, watchedValue]);

    const setRefs = (el: HTMLTextAreaElement | null) => {
      innerRef.current = el;
      if (typeof ref === "function") {
        ref(el);
      } else if (ref) {
        (ref as React.MutableRefObject<HTMLTextAreaElement | null>).current = el;
      }
    };

    return (
      <textarea
        {...rest}
        ref={setRefs}
        rows={1}
        style={{ resize: "none", ...style }}
        onInput={(e) => {
          resize();
          onInput?.(e);
        }}
        onFocus={(e) => {
          setFocused(true);
          onFocus?.(e);
        }}
        onBlur={(e) => {
          setFocused(false);
          onBlur?.(e);
        }}
      />
    );
  }
);

export default AutoGrowTextarea;
